import _ from 'lodash';
import platformUtils from './platformUtils';
import * as members from './privateMembers';
import {isStageUnfriendly} from './utils/livePreview';

const wrappedClasses = {};
let raven;
const JS_WIXCODE_SDK_SENTRY_DSN = 'https://d83ddb9b6f0643068176ec3ed0a94686@sentry.io/271739';

function dataCallback(data) {
    const exceptionFrames = _.get(data, ['exception', 'values', 0, 'stacktrace', 'frames'], []);
    if (exceptionFrames.length) {
        _.set(data, ['exception', 'values', 0, 'stacktrace', 'frames'], _.map(exceptionFrames, (frame) => {
            if (frame && _.isString(frame.filename)) {
                frame.abs_path = frame.filename;
                frame.filename = _.last(_.split(frame.filename, '/'));
            }
            return frame;
        }));
        const lastFrame = _.last(exceptionFrames);
        data.fingerprint = _.concat(data.fingerprint || [], [lastFrame.filename, lastFrame.lineno, lastFrame.colno]);
    }

    return data;
}

function setUserContext() {
    raven.setUserContext({
        id: _.get(self, ['wix-users', 'currentUser', 'id']),
        baseUrl: _.get(self, ['wix-location', 'baseUrl'])
    });
}

function initRaven() {
    const monitoring = platformUtils.getMonitoringServices();
    raven = monitoring.createMonitor(JS_WIXCODE_SDK_SENTRY_DSN, dataCallback);
}

function tryToCreateNewInstance(Class) {
    return (...args) => {
        try {
            platformUtils.wSpyLog('createInstance', [Class, ...args]);
            return new Class(...args);
        } catch (error) {
            raven.setExtraContext(args);
            raven.captureException(error);
            throw error;
        }
    };
}

function tryToRunMethod(method, getRavenContext, descriptor) {
    return function (...args) {
        try {
            const logName = descriptor === 'get' ? 'get' : 'runMethod';
            platformUtils.wSpyLog(logName, [descriptor || method.name, this, ...args]);
            if (isStageUnfriendly(method) && platformUtils.getRGI().getLivePreviewMode && platformUtils.getRGI().getLivePreviewMode()) {
                platformUtils.wSpyLog(logName, [descriptor || method.name, 'blocked for livepreview']);
                return;
            }
            return method.apply(this, args);
        } catch (error) {
            const {originalError} = error;
            if(error instanceof MonitoringErrorToIgnore) {
                throw originalError;
            }
            const context = _.isFunction(getRavenContext) ? getRavenContext.call(this) : {};
            raven.setExtraContext(context);
            raven.captureException(error);
            throw error;
        }
    };
}

// Note: this function travels from the given class until Object and wrapped all super classes.
// This function saves the wrapped classes and returns a given class(or super class) if a class with the same name was already wrapped
function monitorClassIncludingSuperClasses(Class, getRavenContext, allowTypeOverride) {
    if (!Class instanceof Object || !Class.prototype) {
        return Class;
    }

    const className = _.get(Class.prototype, 'constructor.name');

    if (!className) {
        return Class;
    }

    let wrappedClass = allowTypeOverride ? null : wrappedClasses[className];

    if (wrappedClass) {
        return wrappedClass;
    }

    const superClass = Object.getPrototypeOf(Class);
    monitorClassIncludingSuperClasses(superClass, getRavenContext, allowTypeOverride);

    wrappedClass = exportObj.monitorClass(Class, getRavenContext);
    wrappedClasses[className] = wrappedClass;

    return wrappedClass;
}

function monitorClass(Class, getRavenContext) {
    if (!Class instanceof Object || !Class.prototype) {
        return Class;
    }

    const compDescriptors = Object.getOwnPropertyDescriptors(Class.prototype);
    for (const descriptor in compDescriptors) { //eslint-disable-line guard-for-in
        const compDescriptor = compDescriptors[descriptor];
        if (descriptor === 'constructor') {
            // do nothing
        } else if (compDescriptor.set || compDescriptor.get) {
            let getWrapper;
            let setWrapper;
            if (compDescriptor.set) {
                setWrapper = tryToRunMethod(compDescriptor.set, getRavenContext, descriptor);
            }
            if (compDescriptor.get) {
                getWrapper = tryToRunMethod(compDescriptor.get, getRavenContext, descriptor);
            }
            Object.defineProperty(Class.prototype, descriptor, {configurable: true, get: getWrapper, set: setWrapper});
        } else if (_.isFunction(compDescriptor.value)) {
            Object.defineProperty(Class.prototype, descriptor, {
                configurable: true,
                value: tryToRunMethod(compDescriptor.value, getRavenContext, descriptor)
            });
        }
    }

    return tryToCreateNewInstance(Class);
}

function getRavenContextForComponent() {
    const RMI = members._RMI.get(this);
    const contextId = members._contextId.get(this);
    const compId = members._compId.get(this);
    const role = members._role.get(this);
    const controllerId = members._controllerId.get(this);
    const type = members._type.get(this);

    return {compId, type, contextId, role, controllerId, RMI};
}

function monitorComponent(Class, allowTypeOverride) {
    return monitorClassIncludingSuperClasses(Class, exportObj.getRavenContextForComponent, allowTypeOverride);
}

function getRavenContextForNamespace() {
    const appDefId = members._appDefId.get(this);

    return {appDefId};
}

function monitorNamespace(Class) {
    return exportObj.monitorClass(Class, exportObj.getRavenContextForNamespace);
}

function reportError(e) {
    if (raven) {
        raven.captureException(e);
    }
}

class MonitoringErrorToIgnore extends Error {
    constructor(originalError) {
        super();

        if (Error.captureStackTrace) {
          Error.captureStackTrace(this, MonitoringErrorToIgnore);
        }

        if (typeof originalError === "string") {
            this.error = new Error(originalError);
        } else {
            this.error = originalError;
        }
    }
    get originalError() {
        return this.error;
    }
}

const exportObj = {
    initRaven,
    monitorClass,
    monitorComponent,
    monitorNamespace,
    reportError,
    getRavenContextForComponent,
    getRavenContextForNamespace,
    setUserContext,
    MonitoringErrorToIgnore
};

export default exportObj;
